export const GLOBAL_COORD_ADDRESS_CACHE_KEY = '__GLOBAL_COORD_ADDRESS_MAP__'

export function getGlobalCoordAddressCache() {
  if (typeof window === 'undefined') return new Map()
  if (!(window[GLOBAL_COORD_ADDRESS_CACHE_KEY] instanceof Map)) {
    window[GLOBAL_COORD_ADDRESS_CACHE_KEY] = new Map()
  }
  return window[GLOBAL_COORD_ADDRESS_CACHE_KEY]
}

export function normalizeCoordKey(lon, lat) {
  const lonNum = Number(lon)
  const latNum = Number(lat)
  if (!Number.isFinite(lonNum) || !Number.isFinite(latNum)) return ''
  return `${lonNum.toFixed(6)},${latNum.toFixed(6)}`
}

export function isLikelyRawLonLatPairText(text) {
  if (text == null) return false
  const value = String(text).trim()
  if (!value || /[\u4e00-\u9fff]/.test(value)) return false
  const match = value.match(/^(-?\d+(?:\.\d+)?)\s*[,，]\s*(-?\d+(?:\.\d+)?)$/)
  if (!match) return false
  const longitude = Number(match[1])
  const latitude = Number(match[2])
  return Number.isFinite(longitude) && Number.isFinite(latitude)
    && Math.abs(longitude) <= 180 && Math.abs(latitude) <= 90
}

export function isValidResolvedAddress(address) {
  if (address == null) return false
  const value = String(address).trim()
  if (!value || isLikelyRawLonLatPairText(value)) return false
  return !['—', '--', '未知位置', '暂无数据', '鼠标移上获取位置...'].includes(value)
}

export function reverseGeocodeByAmap(lon, lat, { AMap, geocoder } = {}) {
  const longitude = Number(lon)
  const latitude = Number(lat)
  if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return Promise.resolve('')
  const amap = AMap || (typeof window !== 'undefined' ? window.AMap : null)
  if (!amap) return Promise.resolve('')

  const run = target => new Promise(resolve => {
    if (!target || typeof target.getAddress !== 'function') return resolve('')
    target.getAddress([longitude, latitude], (status, result) => {
      const address = status === 'complete' && result && result.regeocode
        ? String(result.regeocode.formattedAddress || '').trim()
        : ''
      resolve(address)
    })
  })

  try {
    if (geocoder) return run(geocoder)
    if (amap.Geocoder) return run(new amap.Geocoder({}))
    if (typeof amap.plugin === 'function') {
      return new Promise(resolve => {
        amap.plugin('AMap.Geocoder', async () => resolve(await run(new amap.Geocoder({}))))
      })
    }
  } catch (e) {
    return Promise.resolve('')
  }
  return Promise.resolve('')
}

export async function resolveAddressByCoord(
  lon,
  lat,
  { coordKey, getLocalCache, setLocalCache, AMap, geocoder } = {}
) {
  const key = coordKey || normalizeCoordKey(lon, lat)
  if (!key) return ''
  const localValue = typeof getLocalCache === 'function' ? getLocalCache(key) : ''
  if (isValidResolvedAddress(localValue)) return String(localValue).trim()

  const globalCache = getGlobalCoordAddressCache()
  const cachedValue = globalCache.get(key)
  if (isValidResolvedAddress(cachedValue)) {
    const address = String(cachedValue).trim()
    if (typeof setLocalCache === 'function') setLocalCache(key, address)
    return address
  }

  const resolved = await reverseGeocodeByAmap(lon, lat, { AMap, geocoder })
  if (!isValidResolvedAddress(resolved)) return ''
  const address = String(resolved).trim()
  globalCache.set(key, address)
  if (typeof setLocalCache === 'function') setLocalCache(key, address)
  return address
}
